import { Host, h, Component, Element, Prop, Event, EventEmitter } from '@stencil/core';
import state, { store } from '../../state';

import type { ScreenContext, EmailContent, SMSContent, BaseContent, SuccessContent } from '../../types';
import type { NotificationContent } from '../../../notification-overlay/types';

type CustomFieldElement = {
  field: 'customFields';
  index: number;
};

export type HighlightableElement =
  | {
      field:
        | keyof EmailContent
        | keyof SMSContent
        | keyof BaseContent
        | keyof SuccessContent
        | keyof NotificationContent
        | null;
    }
  | CustomFieldElement;

function isCustomFieldElement(element: HighlightableElement): element is CustomFieldElement {
  return element?.field === 'customFields';
}

@Component({
  tag: 'om-clickable-element',
  styleUrl: './clickable-element.css',
  scoped: true,
})
export class ClickableElement {
  @Element() el: HTMLElement;
  @Prop() element: HighlightableElement;
  @Prop() containerRef?: (el: HTMLElement) => void;
  @Prop() overlayState?: any;
  @Prop() overlayStore?: any;
  @Prop() disabled?: boolean = false;

  private getCurrentState() {
    return this.overlayState || state;
  }

  private getCurrentStore() {
    return this.overlayStore || store;
  }

  @Event({
    eventName: 'clickedEditableElement',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  clickedEditableElement: EventEmitter<{
    currentScreen: ScreenContext;
    element: HighlightableElement;
  }>;

  private clickedEditableElementHandler(e: MouseEvent, element: HighlightableElement) {
    const currentState = this.getCurrentState();

    if (currentState.mode !== 'preview') {
      if (this.el.onclick) {
        this.el.onclick(e);
      }
      return;
    }

    if (currentState.preventHighlight) {
      return;
    }

    if (this.disabled) {
      return;
    }

    if (isCustomFieldElement(element) && isCustomFieldElement(currentState.highlightElement)) {
      currentState.highlightElement =
        currentState.highlightElement.index === element.index ? { field: null } : (element as HighlightableElement);
    } else {
      currentState.highlightElement =
        currentState.highlightElement?.field === element.field ? { field: null } : (element as HighlightableElement);
    }

    this.clickedEditableElement?.emit({
      element: currentState.highlightElement,
      currentScreen: currentState.currentScreen,
    });
  }

  private handleHighlightElement() {
    const currentState = this.getCurrentState();
    let shouldHighlight = false;

    if (isCustomFieldElement(this.element) && isCustomFieldElement(currentState.highlightElement)) {
      shouldHighlight = currentState.highlightElement.index === this.element.index;
    } else {
      shouldHighlight = currentState.highlightElement?.field === this.element.field;
    }

    this.el.classList[shouldHighlight ? 'add' : 'remove']('highlighted');
  }

  render() {
    return (
      <Host ref={this.containerRef} onClick={(e: MouseEvent) => this.clickedEditableElementHandler(e, this.element)}>
        <slot></slot>
      </Host>
    );
  }

  componentWillLoad() {
    const currentStore = this.getCurrentStore();
    currentStore.onChange('highlightElement', () => {
      this.handleHighlightElement();
    });
  }

  componentWillRender() {
    const currentState = this.getCurrentState();
    if (currentState.mode === 'preview' && !currentState.preventHighlight && !this.disabled) {
      this.el.classList.add('clickable');
    }
  }

  componentDidRender() {
    this.handleHighlightElement();
  }
}
